import _, { get, merge, set, uniqBy } from "lodash"
import { deepmerge } from "@mui/utils"
import { getCenterFromGeometry } from "utils/geometry"
import { calculateDisplacement } from "./step"
import { point } from "@turf/helpers"

import {
  AnchorData,
  AmenityData,
  Opening,
  Unit,
  Relationship,
  TraversalRelationship,
  ElevatorRelationship,
  StairsRelationship,
  RampRelationship,
  EscalatorRelationship,
  UnitData,
  KioskData,
  SectionData,
} from "../../../types"
import {
  Graph,
  IGraphFeatureOptions,
  IGraphOptions,
  IUnitDistanceModifier,
  IVerticalGraphOptions,
} from "../type"

import {
  ELEVATOR_DISTANCE,
  ESCALATOR_DISTANCE,
  BASE_POI_DISTANCE,
  STAIR_DISTANCE,
  RAMP_DISTANCE,
} from "../constants"

type UnitConnection = Record<string, Opening[]>

type FeatureObject = Record<
  string,
  Opening | AnchorData | AmenityData | UnitData
>
interface IPrepareSteps {
  relationships: Relationship[]
  openings: Opening[]
  units: Unit[]
  kiosks: KioskData[]
  anchors: AnchorData[]
  amenities: AmenityData[]
  sections: SectionData[]
  featuresObj: any
  graphOptions?: IGraphFeatureOptions
}

const mergeGraphs = (graphs: Graph[]) => {
  return graphs.reduce(
    (graph, currentGraph) => deepmerge(graph, currentGraph),
    {}
  )
}

const calculateFeatureDistanceWithinUnit = (
  features: (Opening | AnchorData | AmenityData)[],
  option?: undefined | { modifier: number }
): Graph => {
  const { modifier = 0 } = option || { modifier: 0 }
  let relationshipGraph = {}

  for (let currentIndex = 0; currentIndex < features.length; currentIndex++) {
    const isLastItem = currentIndex + 1 === features.length
    if (isLastItem) break // Skip the last item

    for (let j = currentIndex + 1; j < features.length; j++) {
      const opening = features[currentIndex]
      const openingInUnit = features[j]

      try {
        const distance =
          calculateDisplacement(opening.geometry, openingInUnit.geometry, {
            units: "meters",
          }) + modifier

        if (opening.id === openingInUnit.id) continue // Skip if IDs are the same

        set(relationshipGraph, `${opening.id}.${openingInUnit.id}`, distance)
        set(relationshipGraph, `${openingInUnit.id}.${opening.id}`, distance)
      } catch (error) {
        // Error handling: in case of an error, continue to the next iteration
        continue
      }
    }
  }
  return relationshipGraph
}

export const getUnitConnection = (
  units: Unit[],
  relationships: TraversalRelationship[]
): UnitConnection => {
  const openingConnections = {}
  const relationshipMap = new Map()

  // Preprocess relationships for quick access
  relationships.forEach((relationship) => {
    const originId = relationship.properties.origin?.id || null
    const destinationId = relationship.properties.destination?.id || null

    if (!relationshipMap.has(originId)) {
      relationshipMap.set(originId, [])
    }
    if (!relationshipMap.has(destinationId)) {
      relationshipMap.set(destinationId, [])
    }

    relationshipMap.get(originId).push(relationship)
    relationshipMap.get(destinationId).push(relationship)
  })
  // Iterate units and build connections
  units.forEach((unit) => {
    const unitId = unit.id
    const connectedRelations = relationshipMap.get(unitId) || []

    const relationIntermediary = connectedRelations.map(
      (relationship) => relationship.properties.intermediary[0] // Assuming intermediary is always an array
    )

    openingConnections[unitId] = uniqBy(
      [...(openingConnections[unitId] || []), ...relationIntermediary],
      "id"
    )
  })
  return openingConnections
}

/**
 *  Unit distance modifier used in traversal graph calculations.
 *  Example: Units categorized as "room" or "unenclosedarea" often avoid being used as routing paths.
 *    {
 *        room: 1000,
 *        unenclosedarea: 1000
 *    }
 */
const MODIFIER_UNIT_CATEGORIES = {
  room: 1000,
  terrace: 1000,
  unenclosedarea: 1000,
}
const createTraversalLikeGraph = (
  unitConnects: UnitConnection,
  featuresObj: FeatureObject,
  unitDistanceModifier: IUnitDistanceModifier = {}
): Graph => {
  const unitDistanceModifierConfig = _.merge(
    {},
    MODIFIER_UNIT_CATEGORIES,
    unitDistanceModifier
  )
  const traversalGraph = _(unitConnects).reduce(
    (graph: Graph, openings: Opening[], key: string) => {
      const unit = featuresObj[key] as UnitData | undefined
      const modifier = get(
        unitDistanceModifierConfig,
        unit?.properties?.category,
        0
      )
      return deepmerge(
        graph,
        calculateFeatureDistanceWithinUnit(openings, { modifier })
      )
    },
    {}
  )
  return traversalGraph
}

// Case Escalator / Ramp Relationships
const createEscalatorLikeGraph = (
  relationships: Relationship[],
  options: IGraphOptions = {}
): Graph => {
  const { baseDistance = ESCALATOR_DISTANCE, multiplier = 1 } = options || {}
  return relationships.reduce((acc, relationship) => {
    const {
      properties: { direction, origin, destination },
    } = relationship
    // Initialize graph for current relationship
    const graph = set(
      {},
      `${origin.id}.${destination.id}`,
      baseDistance * multiplier
    )
    // Add reverse direction if undirected
    if (direction === "undirected") {
      set(graph, `${destination.id}.${origin.id}`, baseDistance * multiplier)
    }
    return deepmerge(acc, graph)
  }, {})
}

const createElevatorLikeGraph = (
  elevatorLikeRelationships: ElevatorRelationship[] | StairsRelationship[],
  traversalRelationships: TraversalRelationship[],
  options: IVerticalGraphOptions = {}
) => {
  const {
    baseDistance = ELEVATOR_DISTANCE,
    multiplier = 1,
    scaleDistanceByLevel = false,
  } = options || {}
  const elevatorsArr: Unit[][] = elevatorLikeRelationships.map(
    (relationship: ElevatorRelationship | StairsRelationship) => {
      const { properties } = relationship
      const { origin, intermediary = [], destination } = properties
      return [origin, ...intermediary, destination] as Unit[]
    }
  )

  const elevatorOpenings: Opening[][] = elevatorsArr.map((elevators: Unit[]) =>
    _(elevators)
      .map((elevator) => {
        const relationshipFound = traversalRelationships.find(
          (relationship) => {
            const originId = _.get(relationship, "properties.origin.id")
            const destinationId = _.get(
              relationship,
              "properties.destination.id"
            )
            return [originId, destinationId].includes(elevator.id)
          }
        )
        return relationshipFound
      })
      .compact()
      .map((relationship) => {
        // Find An Opening Connected to the elevator
        return _.first(
          _.get(relationship, "properties.intermediary")
        ) as Opening
      })
      .value()
  )

  // Connect the nodes of the stacking elevator's opening
  // Example
  // elevator-1 connected with opening-1
  // elevator-2 connected with opening-2
  // elevator-3 connected with opening-3
  // Therefore opening-1 should connected with opening-2 and opening-3
  // {
  //   opening-1 {
  //     opening-2 : ELEVATOR_DISTANCE,
  //     opening-3 : ELEVATOR_DISTANCE,
  //   },
  //   opening-2 {
  //     opening-1 : ELEVATOR_DISTANCE,
  //     opening-3 : ELEVATOR_DISTANCE,
  //   },
  //   opening-3 {
  //     opening-1 : ELEVATOR_DISTANCE,
  //     opening-2 : ELEVATOR_DISTANCE,
  //   },
  // }
  const elevatorOpeningSelfConnectedGraph: Graph[] = elevatorOpenings.map(
    (elevatorOpenings) => {
      const elevatorGraph = elevatorOpenings.reduce(
        (acc, opening, currentIndex, array) => {
          if (currentIndex + 1 === array.length) return acc
          const openingToConnect = array.slice(currentIndex + 1)
          const relationshipGraph = openingToConnect.reduce(
            (acc, openingInUnit) => {
              if (!opening || !openingInUnit) return acc
              let distance = baseDistance * multiplier
              if (
                scaleDistanceByLevel &&
                _.isNumber(opening.properties.ordinal) &&
                _.isNumber(openingInUnit.properties.ordinal)
              ) {
                const levelDifference = Math.abs(
                  opening.properties.ordinal - openingInUnit.properties.ordinal
                )
                if (levelDifference > 0) distance *= levelDifference
              }
              _.set(acc, `${opening.id}.${openingInUnit.id}`, distance)
              _.set(acc, `${openingInUnit.id}.${opening.id}`, distance)
              return acc
            },
            {}
          )
          return deepmerge(acc, relationshipGraph)
        },
        {}
      )
      return elevatorGraph
    },
    {}
  )

  return elevatorOpeningSelfConnectedGraph.reduce(
    (graph, elevatorGraph) => deepmerge(graph, elevatorGraph),
    {}
  )
}

export const addPOIDistanceFromOpenings = (
  feature: AnchorData | AmenityData | UnitData | KioskData | SectionData,
  openings: Opening[],
  graph: Graph = {}
) => {
  for (let i = 0; i < openings.length; i++) {
    const opening = openings[i]
    try {
      const { geometry: featureCenterGeometry } = point(
        getCenterFromGeometry(feature.geometry)
      )
      const { geometry: openingCenterGeometry } = point(
        getCenterFromGeometry(opening.geometry)
      )
      const distance =
        calculateDisplacement(featureCenterGeometry, openingCenterGeometry, {
          units: "meters",
        }) + BASE_POI_DISTANCE

      _.set(graph, `${opening.id}.${feature.id}`, distance)
      _.set(graph, `${feature.id}.${opening.id}`, distance)
    } catch (error) {
      console.log("error calculating distance for poi id: " + feature.id)
      continue
    }
  }
  return graph
}

// Anchor
const createAnchorDistanceGraph = (
  anchors: AnchorData[],
  unitOpenings: UnitConnection
): Graph => {
  let graph = {}
  for (let i = 0; i < anchors.length; i++) {
    try {
      const anchor = anchors[i]
      const unit = _.get(anchor, "properties.unit")
      if (!unit) return graph

      const connectedOpenings = unitOpenings[unit.id]
      if (!connectedOpenings) return graph

      addPOIDistanceFromOpenings(anchor, connectedOpenings, graph)
    } catch (error) {
      continue
    }
  }
  return graph
}

// Unit
const createUnitDistanceGraph = (
  units: UnitData[],
  unitOpenings: UnitConnection
): Graph => {
  let graph = {}
  for (let i = 0; i < units.length; i++) {
    try {
      const unit = units[i]
      if (!unit) continue

      const connectedOpenings = unitOpenings[unit.id]
      if (!connectedOpenings) continue

      addPOIDistanceFromOpenings(unit, connectedOpenings, graph)
    } catch (error) {
      continue
    }
  }
  return graph
}

// Kiosk
const createKioskDistanceGraph = (
  kiosks: KioskData[],
  unitOpenings: UnitConnection
): Graph => {
  let graph = {}
  for (let i = 0; i < kiosks.length; i++) {
    try {
      const kiosk = kiosks[i]

      const unitId = _.get(kiosk, "properties.unit.id")
      if (!unitId) continue

      const connectedOpenings = unitOpenings[unitId]
      if (!connectedOpenings) continue
      addPOIDistanceFromOpenings(kiosk, connectedOpenings, graph)
    } catch (error) {
      continue
    }
  }
  return graph
}

const createAmenityDistanceGraph = (
  amenities: AmenityData[],
  unitOpenings: UnitConnection
) => {
  let acc = {}
  for (let i = 0; i < amenities.length; i++) {
    try {
      const amenity = amenities[i]
      const unit = _.get(amenity, "properties.units[0]")
      if (!unit) return acc
      const connectedOpenings = unitOpenings[unit.id]
      if (!connectedOpenings) return acc
      addPOIDistanceFromOpenings(amenity, connectedOpenings, acc)
    } catch (error) {
      continue
    }
  }
  return acc
}
const createSectionDistanceGraph = (
  sections: SectionData[],
  unitOpenings: UnitConnection
) => {
  let acc = {}
  for (let i = 0; i < sections.length; i++) {
    try {
      const section = sections[i]
      const unit = _.get(section, "properties.unit")
      if (!unit) return acc
      const connectedOpenings = unitOpenings[unit.id]
      if (!connectedOpenings) return acc
      addPOIDistanceFromOpenings(section, connectedOpenings, acc)
    } catch (error) {
      continue
    }
  }
  return acc
}

const createPOIGraph = (
  amenities: AmenityData[],
  anchors: AnchorData[],
  units: UnitData[],
  kiosks: KioskData[],
  sections: SectionData[],
  unitConnects: UnitConnection
): Graph => {
  const anchorDistanceGraph = createAnchorDistanceGraph(anchors, unitConnects)
  const amenityDistanceGraph = createAmenityDistanceGraph(
    amenities,
    unitConnects
  )
  const sectionDistanceGraph = createSectionDistanceGraph(
    sections,
    unitConnects
  )

  const unitPOIDistanceGraph = createUnitDistanceGraph(units, unitConnects)
  const kioskPOIDistanceGraph = createKioskDistanceGraph(kiosks, unitConnects)
  return mergeGraphs([
    anchorDistanceGraph,
    amenityDistanceGraph,
    sectionDistanceGraph,
    unitPOIDistanceGraph,
    kioskPOIDistanceGraph,
  ])
}

const DEFAULT_GRAPH_FEATURE_OPTIONS: IGraphFeatureOptions = {
  traversal: {
    room: 1000,
    terrace: 1000,
    unenclosedarea: 1000,
  },
  escalator: { baseDistance: ESCALATOR_DISTANCE, multiplier: 1 },
  ramp: { baseDistance: RAMP_DISTANCE, multiplier: 1 },
  elevator: { baseDistance: ELEVATOR_DISTANCE, multiplier: 1 },
  stairs: {
    baseDistance: STAIR_DISTANCE,
    multiplier: 1,
    scaleDistanceByLevel: true,
  },
}

export const prepareSteps = ({
  amenities = [],
  anchors = [],
  relationships = [],
  units = [],
  kiosks = [],
  sections = [],
  featuresObj = {},
  graphOptions = {},
}: IPrepareSteps) => {
  const {
    traversal: traversalGraphOptions,
    escalator: escalatorGraphOptions,
    ramp: rampGraphOptions,
    elevator: elevatorGraphOptions,
    stairs: stairsGraphOptions,
  } = merge({}, DEFAULT_GRAPH_FEATURE_OPTIONS, graphOptions || {})

  const {
    traversal: traversalRelationships = [],
    escalator: escalatorRelationships = [],
    ramp: rampRelationships = [],
    elevator: elevatorRelationships = [],
    stairs: stairsRelationships = [],
  } = _.groupBy(relationships, "properties.category") as {
    traversal?: TraversalRelationship[]
    escalator?: EscalatorRelationship[]
    ramp?: RampRelationship[]
    elevator?: ElevatorRelationship[]
    stairs?: StairsRelationship[]
  }

  const unitConnects = getUnitConnection(units, traversalRelationships)

  const traversalDistanceGraph = createTraversalLikeGraph(
    unitConnects,
    featuresObj,
    traversalGraphOptions
  )

  const escalatorDistanceGraph = createEscalatorLikeGraph(
    escalatorRelationships,
    escalatorGraphOptions
  )

  const rampDistanceGraph = createEscalatorLikeGraph(
    rampRelationships,
    rampGraphOptions
  )

  const elevatorDistanceGraph = createElevatorLikeGraph(
    elevatorRelationships,
    traversalRelationships,
    elevatorGraphOptions
  )

  const stairDistanceGraph = createElevatorLikeGraph(
    stairsRelationships,
    traversalRelationships,
    stairsGraphOptions
  )

  const pointOfInterestDistanceGraph = createPOIGraph(
    amenities,
    anchors,
    units as UnitData[],
    kiosks,
    sections,
    unitConnects
  )

  return {
    base: mergeGraphs([
      traversalDistanceGraph,
      escalatorDistanceGraph,
      rampDistanceGraph,
      elevatorDistanceGraph,
      stairDistanceGraph,
      pointOfInterestDistanceGraph,
    ]),
    elevator: mergeGraphs([
      traversalDistanceGraph,
      rampDistanceGraph,
      elevatorDistanceGraph,
      pointOfInterestDistanceGraph,
    ]),
  }
}
